import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  DEFAULT_DENSITY,
  DEFAULT_THEME,
  applyAppearance,
  isDensity,
  isTheme,
  type Density,
  type Theme,
} from '@/features/shell/appearance';

/** The five modes the client approved (their "Network" is our Loader). */
export const MODES = ['overview', 'operations', 'maintenance', 'analysis', 'loader'] as const;
export type Mode = (typeof MODES)[number];

export const MODE_LABEL: Record<Mode, string> = {
  overview: 'Overview',
  operations: 'Operations',
  maintenance: 'Maintenance',
  analysis: 'Analysis',
  loader: 'Loader',
};

export type BottomPanelState = 'collapsed' | 'default' | 'expanded';

export interface Selection {
  /** Twin asset id (rf_antenna, rfims02, senseSat_01, ...). */
  assetId: string | null;
  /** Loader mission id (noaa20, ...). */
  missionId: string | null;
  /** Loader antenna id (wallops_26m, ...). */
  antennaId: string | null;
  /** Subsystem id inside the selected asset. */
  subsystemId: string | null;
}

interface UIState {
  stripExpanded: boolean;
  stripPinned: boolean;
  rightPanelOpen: boolean;
  bottomPanel: BottomPanelState;
  bottomTab: string;
  presenter: boolean;
  gaiaOpen: boolean;
  selection: Selection;
  /** Row heights, paddings and table font sizes (CSS variables on the root, persisted). */
  density: Density;
  /** Standard, or the red-shifted night variant for darkened control rooms (data-theme on the root, persisted). */
  theme: Theme;
  setStripExpanded: (v: boolean) => void;
  toggleStripPinned: () => void;
  toggleRightPanel: () => void;
  cycleBottomPanel: () => void;
  setBottomPanel: (s: BottomPanelState) => void;
  setBottomTab: (t: string) => void;
  togglePresenter: () => void;
  setGaiaOpen: (v: boolean) => void;
  select: (partial: Partial<Selection>) => void;
  clearSelection: () => void;
  setDensity: (d: Density) => void;
  setTheme: (t: Theme) => void;
}

export const useUI = create<UIState>()(
  persist(
    (set) => ({
      stripExpanded: false,
      stripPinned: false,
      rightPanelOpen: true,
      bottomPanel: 'collapsed',
      bottomTab: 'gantt',
      presenter: false,
      gaiaOpen: false,
      selection: { assetId: null, missionId: null, antennaId: null, subsystemId: null },
      density: DEFAULT_DENSITY,
      theme: DEFAULT_THEME,
      setStripExpanded: (v) => set({ stripExpanded: v }),
      toggleStripPinned: () => set((s) => ({ stripPinned: !s.stripPinned, stripExpanded: !s.stripPinned })),
      toggleRightPanel: () => set((s) => ({ rightPanelOpen: !s.rightPanelOpen })),
      cycleBottomPanel: () =>
        set((s) => ({
          bottomPanel:
            s.bottomPanel === 'collapsed'
              ? 'default'
              : s.bottomPanel === 'default'
                ? 'expanded'
                : 'collapsed',
        })),
      setBottomPanel: (bottomPanel) => set({ bottomPanel }),
      setBottomTab: (bottomTab) => set({ bottomTab, bottomPanel: 'default' }),
      togglePresenter: () => set((s) => ({ presenter: !s.presenter })),
      setGaiaOpen: (gaiaOpen) => set({ gaiaOpen }),
      select: (partial) => set((s) => ({ selection: { ...s.selection, ...partial } })),
      clearSelection: () =>
        set({ selection: { assetId: null, missionId: null, antennaId: null, subsystemId: null } }),
      setDensity: (density) => set({ density }),
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'gemdt-ui',
      partialize: (s) => ({
        stripPinned: s.stripPinned,
        rightPanelOpen: s.rightPanelOpen,
        bottomPanel: s.bottomPanel,
        bottomTab: s.bottomTab,
        density: s.density,
        theme: s.theme,
      }),
      // The default shallow merge, except that a persisted density or theme this build does not
      // know (an older build's value, a hand edit) falls back to the default with a warning.
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<UIState>;
        const merged = { ...current, ...saved };
        if (!isDensity(merged.density)) {
          console.warn(`[ui] ignored the persisted density ${String(merged.density)}`);
          merged.density = DEFAULT_DENSITY;
        }
        if (!isTheme(merged.theme)) {
          console.warn(`[ui] ignored the persisted theme ${String(merged.theme)}`);
          merged.theme = DEFAULT_THEME;
        }
        return merged;
      },
    },
  ),
);

// Density and theme live on <html> (data-density, data-theme and the density variables) so every
// route, the login page included, and every portal (menus, dialogs, toasts) picks them up.
if (typeof document !== 'undefined') {
  applyAppearance(document.documentElement, useUI.getState());
  useUI.subscribe((s, prev) => {
    if (s.density !== prev.density || s.theme !== prev.theme) applyAppearance(document.documentElement, s);
  });
}

/** One clock for the globe, the twin, the Gantt and the agent. Live follows
 *  wall time; scrub holds a simulated time that advances at `multiplier`. */
export interface PlaybackRange {
  startMs: number;
  endMs: number;
  label: string;
}
interface ClockState {
  live: boolean;
  playing: boolean;
  multiplier: number;
  /** Simulated time in ms since epoch (only meaningful when !live). */
  simTime: number;
  playbackRange: PlaybackRange | null;
  setPlaybackRange: (range: PlaybackRange | null) => void;
  setLive: () => void;
  setSimTime: (ms: number) => void;
  setPlaying: (v: boolean) => void;
  setMultiplier: (m: number) => void;
  jump: (deltaMs: number) => void;
}

export const useClock = create<ClockState>()((set, get) => ({
  live: true,
  playing: true,
  multiplier: 1,
  simTime: Date.now(),
  playbackRange: null,
  setPlaybackRange: (playbackRange) =>
    set(
      playbackRange
        ? { playbackRange, live: false, playing: false, simTime: playbackRange.startMs }
        : { playbackRange },
    ),
  setLive: () => {
    if (!get().playbackRange) set({ live: true, playing: true, multiplier: 1, simTime: Date.now() });
  },
  setSimTime: (ms) => {
    if (!Number.isFinite(ms)) return;
    const r = get().playbackRange;
    set({ live: false, simTime: r ? Math.max(r.startMs, Math.min(r.endMs, ms)) : ms });
  },
  setPlaying: (playing) => set({ playing }),
  setMultiplier: (multiplier) => set({ multiplier }),
  jump: (deltaMs) => {
    const base = get().live ? Date.now() : get().simTime;
    get().setSimTime(base + deltaMs);
  },
}));

/** Current time for every consumer, live or simulated. */
export function clockNow(): number {
  const s = useClock.getState();
  return s.live ? Date.now() : s.simTime;
}

let ticking = false;
/** Advances the simulated clock while playing. Started once by the shell. */
export function startClockTicker(): () => void {
  if (ticking) return () => {};
  ticking = true;
  let last = performance.now();
  let raf = 0;
  const tick = (t: number) => {
    const dt = t - last;
    last = t;
    const s = useClock.getState();
    if (!s.live && s.playing) {
      const next = s.simTime + dt * s.multiplier;
      const r = s.playbackRange;
      const atEnd = r && (next >= r.endMs || next < r.startMs);
      useClock.setState({
        simTime: r ? Math.max(r.startMs, Math.min(r.endMs, next)) : next,
        ...(atEnd ? { playing: false } : {}),
      });
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => {
    cancelAnimationFrame(raf);
    ticking = false;
  };
}
